import { useState, useEffect, SyntheticEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import Icon from '../../../../components/icon'
import apiUrl from '../../../../configs/constant'
import {
    Box,
    Grid,
    Typography,
    Divider,
    Fab,
    Button,
    FormControl,
    TextField,
    FormHelperText,
    Checkbox,
    FormControlLabel,
    Autocomplete,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material'

interface FormInputs {
    syscabang_id: number
    mmbarang_divisi_id: number
    mmbarang_kelompok_id: number
    mmbarang_class_id: number
    mmjenis_barang_id: number
    kode: string
    mmsatuan_id: number
    mmgudang_id: number
    nama_barang: string
    qty_min: number
    is_kain: number
    is_stok: number
    is_batch: number
    is_jasa: number
    is_asset: number
    is_sales: number
    is_added_sales: number
    is_buy: number
    lebar_kain: number
    leadtime: number
}

export default function Add() {
    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token
    const navigate = useNavigate()
    const cabang = JSON.parse(localStorage.getItem('cabang')!)

    // State for select options
    const [divisiOptions, setDivisiOptions] = useState<any[]>([])
    const [kelompokOptions, setKelompokOptions] = useState<any[]>([])
    const [classOptions, setClassOptions] = useState<any[]>([])
    const [jenisOptions, setJenisOptions] = useState<any[]>([])
    const [satuanOptions, setSatuanOptions] = useState<any[]>([])
    const [gudangOptions, setGudangOptions] = useState<any[]>([])
    const [listJenis, setListJenis] = useState<any[]>([])
    const [valJenis, setValJenis] = useState<any>(null)

    // Fetch select options
    useEffect(() => {
        fetchCombo('/mm/divisi-barang/list-combo', setDivisiOptions)
        fetchCombo('/mm/kelompok-barang/list-combo', setKelompokOptions)
        fetchCombo('/mm/kelas-barang/list-combo', setClassOptions)
        fetchCombo('/mm/jenis-barang/list-combo', setListJenis)
        fetchCombo('/mm/satuan/list-combo', setSatuanOptions)
        fetchCombo('/mm/gudang/list-combo', setGudangOptions)
    }, [])

    const fetchCombo = (endpoint: string, setter: (data: any[]) => void) => {
        axios
            .get(apiUrl.apiUrl + endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(res => {
                if (res.data.success) setter(res.data.data)
            })
    }

    const defaultValues: Partial<FormInputs> = {
        syscabang_id: cabang,
        mmbarang_divisi_id: 0,
        mmbarang_kelompok_id: 0,
        mmbarang_class_id: 0,
        mmjenis_barang_id: 0,
        kode: '',
        mmsatuan_id: 0,
        mmgudang_id: 0,
        nama_barang: '',
        qty_min: 0,
        is_kain: 0,
        is_stok: 0,
        is_batch: 0,
        is_jasa: 0,
        is_asset: 0,
        is_sales: 0,
        is_buy: 0,
        is_added_sales: 0,
        leadtime: 45
    }

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm<FormInputs>({ defaultValues })

    const onSubmit = (data: FormInputs) => {
        Swal.fire({
            title: 'Konfirmasi',
            text: 'Apakah anda ingin menyimpan data ini ?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#337ab7',
            cancelButtonColor: '#ffc107',
            confirmButtonText: 'Simpan'
        }).then(result => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Now loading',
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading()
                    }
                })

                const url = apiUrl.apiUrl + '/mm/barang/add'
                axios
                    .post(url, data, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                    .then(response => {
                        if (response.data.success) {
                            Swal.fire({
                                title: 'Pemberitahuan',
                                text: response.data.message,
                                icon: 'success',
                                timer: 1000,
                                timerProgressBar: true,
                                willClose: () => {
                                    navigate('/mm/master-barang')
                                }
                            })
                        } else {
                            Swal.fire({
                                title: 'Pemberitahuan',
                                text: response.data.message,
                                icon: 'error'
                            })
                        }
                    })
                    .catch(error => {
                        Swal.fire({
                            title: 'Pemberitahuan',
                            text: error.message,
                            icon: 'error'
                        })
                    })
            }
        })
    }

    const handleChangeAutocompleteJenis = (event: SyntheticEvent, newValue: any | null) => {
        setValue('mmjenis_barang_id', newValue ? newValue.id : null)
        setValJenis(newValue)
    }

    return (
        <Grid container spacing={6} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Grid item xl={12} md={12} xs={12}>
                <Fab variant='extended' color='primary' sx={{ '& svg': { mr: 1 } }} onClick={() => window.history.back()}>
                    <Icon icon='mdi:arrow-left' />
                    Kembali
                </Fab>
                <div className={'content-block dx-card'} style={{ padding: 20 }}>
                    <Typography variant='h5' sx={{ textAlign: 'center', marginBottom: 2 }}>
                        Buat Master Barang
                    </Typography>
                    <Divider sx={{ m: 0 }} />
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id='divisi-label'>Divisi</InputLabel>
                                    <Controller
                                        name='mmbarang_divisi_id'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                labelId='divisi-label'
                                                label='Divisi'
                                                {...field}
                                                error={Boolean(errors.mmbarang_divisi_id)}
                                            >
                                                {divisiOptions.map(opt => (
                                                    <MenuItem key={opt.id} value={opt.id}>{opt.nama}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.mmbarang_divisi_id && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id='kelompok-label'>Kelompok</InputLabel>
                                    <Controller
                                        name='mmbarang_kelompok_id'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                labelId='kelompok-label'
                                                label='Kelompok'
                                                {...field}
                                                error={Boolean(errors.mmbarang_kelompok_id)}
                                            >
                                                {kelompokOptions.map(opt => (
                                                    <MenuItem key={opt.id} value={opt.id}>{opt.nama}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.mmbarang_kelompok_id && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id='class-label'>Class</InputLabel>
                                    <Controller
                                        name='mmbarang_class_id'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                labelId='class-label'
                                                label='Class'
                                                {...field}
                                                error={Boolean(errors.mmbarang_class_id)}
                                            >
                                                {classOptions.map(opt => (
                                                    <MenuItem key={opt.id} value={opt.id}>{opt.nama}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.mmbarang_class_id && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        value={valJenis}
                                        options={listJenis}
                                        id='site'
                                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                        onChange={handleChangeAutocompleteJenis}
                                        getOptionLabel={(option: any) => option?.nama || ""}   // 👈 penting
                                        renderOption={(props: any, option: any) => {
                                            return (
                                                <li {...props} key={option.id}>
                                                    {option.nama}
                                                </li>
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                label='Jenis Barang'
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='kode'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <TextField
                                                label='Kode'
                                                {...field}
                                                error={Boolean(errors.kode)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    {errors.kode && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id='satuan-label'>Satuan</InputLabel>
                                    <Controller
                                        name='mmsatuan_id'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                labelId='satuan-label'
                                                label='Satuan'
                                                {...field}
                                                error={Boolean(errors.mmsatuan_id)}
                                            >
                                                {satuanOptions.map(opt => (
                                                    <MenuItem key={opt.id} value={opt.id}>{opt.nama}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.mmsatuan_id && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id='gudang-label'>Gudang</InputLabel>
                                    <Controller
                                        name='mmgudang_id'
                                        control={control}
                                        rules={{ required: false }}
                                        render={({ field }) => (
                                            <Select
                                                labelId='gudang-label'
                                                label='Gudang'
                                                {...field}
                                                error={Boolean(errors.mmgudang_id)}
                                            >
                                                {gudangOptions.map(opt => (
                                                    <MenuItem key={opt.id} value={opt.id}>{opt.nama}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.mmgudang_id && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='nama_barang'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <TextField
                                                label='Nama Barang'
                                                {...field}
                                                error={Boolean(errors.nama_barang)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    {errors.nama_barang && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='qty_min'
                                        control={control}
                                        rules={{ required: true, min: 0 }}
                                        render={({ field }) => (
                                            <TextField
                                                label='Qty Min'
                                                type='number'
                                                {...field}
                                                error={Boolean(errors.qty_min)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    {errors.qty_min && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='lebar_kain'
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                label='Lebar Kain'
                                                type='number'
                                                {...field}
                                                error={Boolean(errors.lebar_kain)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    {errors.lebar_kain && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='leadtime'
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                label='Lead Time'
                                                type='number'
                                                {...field}
                                                error={Boolean(errors.leadtime)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    {errors.leadtime && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            {/* Checkbox fields */}
                            <Grid item xs={12} sm={12}>
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_kain'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Kain'
                                />
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_stok'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Stok'
                                />
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_batch'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Batch'
                                />
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_jasa'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Jasa'
                                />
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_asset'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Asset'
                                />
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_sales'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Sales'
                                />
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_added_sales'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Added Sales'
                                />
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name='is_buy'
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    checked={field.value === 1}
                                                    onChange={e => field.onChange(e.target.checked ? 1 : 0)}
                                                />
                                            )}
                                        />
                                    }
                                    label='Is Buy'
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Button variant='contained' type='submit'>
                                        SIMPAN
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </Grid>
        </Grid>
    )
}